import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { useListSurface } from '../../../../components/table/listSurface';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import type { JobPostingListItem } from '../../applicants/applicantTypes';
import { deliberationApi } from '../deliberationApi';
import {
  DELIBERATION_AUDIT_ENTITY_TYPES,
  DELIBERATION_READ_PERMISSION,
  POSTING_STAGE,
  postingStage,
} from '../deliberationTypes';

/**
 * Deliberate's index: EVERY job posting, in three folders (22 Oct 2026) — Pending, Complete, Cancelled.
 * Opening one is where its applicants' scores are read side by side.
 *
 * **Nothing is created here, and nothing is written anywhere in the module**: a posting is made on
 * Vacancies, its applicants and their stages on Applicants. So there is no status axis (the server fixes
 * the set), and History Logs reads the applicant records the scores come from.
 */
export function DeliberationIndexPage() {
  const navigate = useNavigate();

  const fetchPostings = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      deliberationApi.listPostings(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          unit: query.facets.unit?.join(','),
          stage: query.facets.stage?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<JobPostingListItem>
      module="deliberations"
      moduleNoun="job postings"
      breadcrumb={['Human Resources', 'Recruitment', 'Deliberate']}
      columns={COLUMNS}
      /*
        The STAGE folders (product call, 22 Oct 2026) — Pending (no appointment yet), Complete (an appointment
        recorded, or the item filled), Cancelled (ended otherwise) — drawn as the folder nav over the list.
      */
      facets={[
        { key: 'stage', label: 'Status' },
        { key: 'unit', label: 'Office assignment' },
      ]}
      folderNavFacet="stage"
      auditEntityTypes={DELIBERATION_AUDIT_ENTITY_TYPES}
      rowKey={(posting) => posting.id}
      fetch={fetchPostings}
      searchPlaceholder="Search positions, item numbers or offices"
      emptyMessage="No job posting here. A posting opens when a vacant item is tagged for job posting under Vacancies."
      onRowOpen={(posting) => navigate(`/deliberation/${posting.id}`)}
      openPermission={DELIBERATION_READ_PERMISSION}
      // The POSTING's provenance: when the item was tagged, and by whom.
      otherInfo={(posting) => posting}
    />
  );
}

/** The position title — and on the LIST, the item number joined to it, as on Applicants. */
function PostingAnchor({ posting }: { posting: JobPostingListItem }) {
  const onList = useListSurface();

  return <TextCell value={onList ? `${posting.positionTitle} — ${posting.itemNumber}` : posting.positionTitle} />;
}

const COLUMNS: ColumnDef<JobPostingListItem>[] = [
  {
    key: 'positionTitle',
    header: 'Position title',
    width: 240,
    flex: true,
    sortable: true,
    render: (posting) => <PostingAnchor posting={posting} />,
  },
  {
    key: 'itemNumber',
    header: 'Item no.',
    width: 170,
    sortable: true,
    // On the list it rides in the anchor already.
    summary: false,
    render: (posting) => <TextCell value={posting.itemNumber} mono />,
  },
  {
    key: 'salaryGrade',
    header: 'Salary grade',
    width: 130,
    render: (posting) => <TextCell value={posting.salaryGradeLabel} />,
  },
  {
    key: 'monthlyAmount',
    header: 'Monthly salary',
    width: 140,
    align: 'right',
    render: (posting) => <TextCell value={formatMoney(posting.monthlyAmount)} mono />,
  },
  {
    key: 'orgUnitName',
    header: 'Office assignment',
    width: 200,
    sortable: true,
    summary: true,
    render: (posting) => <TextCell value={posting.orgUnitName} />,
  },
  {
    key: 'applicantCount',
    header: 'Applicants',
    width: 110,
    align: 'right',
    sortable: true,
    summary: true,
    render: (posting) => <TextCell value={String(posting.applicantCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    // The row's FOLDER, in the folder's own word — the nav and the pill read ONE classification.
    render: (posting) => {
      const stage = postingStage(posting);
      return stage === POSTING_STAGE.Pending ? (
        <StatusText tone="active">Pending</StatusText>
      ) : (
        <StatusText tone="inactive">{stage === POSTING_STAGE.Complete ? 'Complete' : 'Cancelled'}</StatusText>
      );
    },
  },
];
